import React, { useState, useEffect, useMemo } from 'react';
import { Play, RotateCcw, Monitor, Smartphone, Languages } from 'lucide-react';
import { WhatsAppSimulator } from '@/components/demo/WhatsAppSimulator';
import { DemoDashboard } from '@/components/demo/DemoDashboard';
import { translations, DemoLanguage } from '@/components/demo/demoTranslations';

type DemoPhase = 'idle' | 'whatsapp' | 'transition' | 'dashboard' | 'complete';

export const DemoPage: React.FC = () => {
  const [phase, setPhase] = useState<DemoPhase>('idle');
  const [showControls, setShowControls] = useState(true);
  const [viewMode, setViewMode] = useState<'side-by-side' | 'phone'>('side-by-side');
  const [resetKey, setResetKey] = useState(0);
  const [language, setLanguage] = useState<DemoLanguage>('pt-BR');

  const t = translations[language];

  // Demo script configuration - depends on language
  const DEMO_MESSAGE = useMemo(() => ({
    text: t.whatsappDemo.messageText,
    amount: t.whatsappDemo.amount,
    category: 'food',
    description: t.whatsappDemo.description,
  }), [t]);

  const WHATSAPP_SCRIPT = useMemo(() => [
    {
      text: DEMO_MESSAGE.text,
      type: 'sent' as const,
      delay: 1000,
      typingDuration: 2000,
    },
    {
      text: t.whatsappDemo.responseMessage,
      type: 'received' as const,
      delay: 800,
    },
  ], [DEMO_MESSAGE.text, t]);

  const startDemo = () => {
    setShowControls(false);
    setPhase('whatsapp');
  };

  const resetDemo = () => {
    setPhase('idle');
    setShowControls(true);
    setResetKey((prev) => prev + 1); // Force remount of components
  };

  const handleWhatsAppComplete = () => {
    setPhase('transition');
    setTimeout(() => {
      setPhase('dashboard');
    }, 1000);
  };

  const handleDashboardComplete = () => {
    setPhase('complete');
    setTimeout(() => {
      setShowControls(true);
    }, 1000);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && phase === 'idle') {
        e.preventDefault();
        startDemo();
      }
      if (e.code === 'KeyR') {
        e.preventDefault();
        resetDemo();
      }
      if (e.code === 'KeyH') {
        setShowControls((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex">
      {/* Sidebar Controls */}
      {showControls && (
        <div className="w-64 min-h-screen bg-gray-800/90 backdrop-blur p-4 flex flex-col gap-4 border-r border-gray-700">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">K</span>
            </div>
            <span className="text-white font-medium">KeepNota Demo</span>
          </div>

          {/* Play/Reset buttons */}
          <div className="flex flex-col gap-2">
            <button
              onClick={startDemo}
              disabled={phase !== 'idle' && phase !== 'complete'}
              className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors w-full"
            >
              <Play className="w-4 h-4" />
              {phase === 'idle' ? t.demo.startDemo : t.demo.restart}
            </button>

            <button
              onClick={resetDemo}
              className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors w-full"
            >
              <RotateCcw className="w-4 h-4" />
              {t.demo.reset}
            </button>
          </div>

          <div className="h-px bg-gray-700" />

          {/* View mode */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-2">View Mode</p>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`flex-1 p-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  viewMode === 'side-by-side'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('phone')}
                className={`flex-1 p-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  viewMode === 'phone'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="h-px bg-gray-700" />

          {/* Language toggle */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-2 flex items-center gap-2">
              <Languages className="w-3 h-3" />
              Language
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setLanguage('pt-BR')}
                className={`px-3 py-2 rounded-lg text-sm transition-colors w-full ${
                  language === 'pt-BR'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {language === 'pt-BR' ? 'Português' : 'Portuguese'}
              </button>
              <button
                onClick={() => setLanguage('en-GB')}
                className={`px-3 py-2 rounded-lg text-sm transition-colors w-full ${
                  language === 'en-GB'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {language === 'pt-BR' ? 'Inglês' : 'English'}
              </button>
            </div>
          </div>

          <div className="h-px bg-gray-700" />

          {/* Phase indicator */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-2">{t.demo.phase}</p>
            <p className="text-white font-medium">
              {phase === 'idle' && t.demo.phases.idle}
              {phase === 'whatsapp' && t.demo.phases.whatsapp}
              {phase === 'transition' && t.demo.phases.transition}
              {phase === 'dashboard' && t.demo.phases.dashboard}
              {phase === 'complete' && t.demo.phases.complete}
            </p>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Keyboard hints */}
          <div className="text-gray-500 text-xs space-y-1">
            <div><span className="bg-gray-700 px-1.5 py-0.5 rounded text-gray-400">SPACE</span> {t.demo.hints.start}</div>
            <div><span className="bg-gray-700 px-1.5 py-0.5 rounded text-gray-400">R</span> {t.demo.hints.reset}</div>
            <div><span className="bg-gray-700 px-1.5 py-0.5 rounded text-gray-400">H</span> {t.demo.hints.hideControls}</div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-8">
        {viewMode === 'side-by-side' ? (
          /* Side by side view */
          <div className="flex items-center gap-8">
            {/* WhatsApp */}
            <div
              className={`transform transition-all duration-700 ${
                phase === 'dashboard' || phase === 'complete'
                  ? 'opacity-70 scale-95'
                  : 'opacity-100 scale-100'
              }`}
            >
              <WhatsAppSimulator
                key={`whatsapp-${resetKey}-${language}`}
                messages={WHATSAPP_SCRIPT}
                onComplete={handleWhatsAppComplete}
                autoStart={phase === 'whatsapp'}
                translations={t}
              />
            </div>

            {/* Arrow */}
            <div
              className={`flex flex-col items-center gap-2 transition-all duration-500 ${
                phase === 'transition' || phase === 'dashboard' || phase === 'complete'
                  ? 'opacity-100 text-green-400'
                  : 'opacity-30 text-white'
              }`}
            >
              <div className="text-4xl">→</div>
              <span className="text-sm">
                {phase === 'transition' || phase === 'dashboard' || phase === 'complete'
                  ? t.demo.registered
                  : t.demo.automatic}
              </span>
            </div>

            {/* Dashboard */}
            <div
              className={`transform transition-all duration-700 ${
                phase === 'dashboard' || phase === 'complete'
                  ? 'opacity-100 scale-100'
                  : 'opacity-70 scale-95'
              }`}
            >
              <DemoDashboard
                key={`dashboard-${resetKey}-${language}`}
                newTransaction={
                  phase === 'dashboard'
                    ? {
                        description: DEMO_MESSAGE.description,
                        amount: DEMO_MESSAGE.amount,
                        category: DEMO_MESSAGE.category,
                      }
                    : undefined
                }
                onAnimationComplete={handleDashboardComplete}
                translations={t}
              />
            </div>
          </div>
        ) : (
          /* Phone only view - shows one at a time */
          <div className="relative">
            {/* WhatsApp */}
            <div
              className={`transform transition-all duration-700 ${
                phase === 'dashboard' || phase === 'complete'
                  ? 'opacity-0 scale-95 absolute'
                  : 'opacity-100 scale-100'
              }`}
            >
              {phase !== 'dashboard' && phase !== 'complete' && (
                <WhatsAppSimulator
                  key={`whatsapp-phone-${resetKey}-${language}`}
                  messages={WHATSAPP_SCRIPT}
                  onComplete={handleWhatsAppComplete}
                  autoStart={phase === 'whatsapp'}
                  translations={t}
                />
              )}
            </div>

            {/* Dashboard */}
            <div
              className={`transform transition-all duration-700 ${
                phase === 'dashboard' || phase === 'complete'
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-95 absolute'
              }`}
            >
              {(phase === 'dashboard' || phase === 'complete') && (
                <DemoDashboard
                  newTransaction={{
                    description: DEMO_MESSAGE.description,
                    amount: DEMO_MESSAGE.amount,
                    category: DEMO_MESSAGE.category,
                  }}
                  onAnimationComplete={handleDashboardComplete}
                  translations={t}
                />
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default DemoPage;
